import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { pick, pipe, propOr, __, props, filter, join } from 'ramda';
import PropTypes from 'prop-types';

import {
  usePatientsActions,
  useAlerts,
  IS_EMERGENCY,
  TITLE,
  FIRST_NAME,
  LAST_NAME,
  GENDER,
  INSTITUTION,
  CARE_OF,
  ADDRESS,
  ZIP_CODE,
  CITY,
  CANTON,
  COUNTRY,
  EMAIL,
  CONTACT_TYPE,
  OTHER_CONTACTS,
} from 'store/patients';
import { ID, RECEIVER_TYPE, INDEX } from 'store/reports';
import { POSITION_MAP } from 'utils/constants';
import Select from 'components/SelectNew';

import { Container, Button } from './styles';

const ContactPanel = ({ type, patient }) => {
  const [index, setIndex] = useState();
  const { fetchPatientReceivers } = usePatientsActions();
  const { action, loading, success, resetAlerts } = useAlerts(fetchPatientReceivers);
  const { setValues } = useFormikContext();
  const contacts = useMemo(
    () =>
      type === CONTACT_TYPE
        ? (patient[OTHER_CONTACTS] || []).map((item, i) => ({
            id: String(i),
            label: pipe(
              ($) => [pipe(propOr('', TITLE), propOr('', __, POSITION_MAP))($), ...props([FIRST_NAME, LAST_NAME], $)],
              filter(Boolean),
              join(' ')
            )(item),
          }))
        : [],
    [patient, type]
  );

  const onClick = useCallback(
    () => action({ [ID]: patient[ID], [RECEIVER_TYPE]: type, ...(index && { [INDEX]: Number(index) }) }),
    [action, index, patient, type]
  );
  const onSelect = useCallback((v) => setIndex(v), [setIndex]);

  useEffect(() => {
    if (typeof success === 'object' && success !== null) {
      setValues(
        pick(
          [
            IS_EMERGENCY,
            TITLE,
            FIRST_NAME,
            LAST_NAME,
            GENDER,
            INSTITUTION,
            CARE_OF,
            ADDRESS,
            ZIP_CODE,
            CITY,
            CANTON,
            COUNTRY,
            EMAIL,
          ],
          success
        )
      );
    }

    resetAlerts();
  }, [success, resetAlerts, setValues]);

  useEffect(() => {
    if (contacts.length === 1) {
      setIndex('0');
    } else {
      setIndex();
    }
  }, [contacts.length, type]);

  return (
    <Container>
      {type === CONTACT_TYPE && <Select value={index} options={contacts} onSelect={onSelect} />}
      <Button type="button" onClick={onClick} disabled={Boolean(loading || success) || (type === CONTACT_TYPE && !index)}>
        Kontaktdaten aus Patientendossier laden
      </Button>
    </Container>
  );
};

ContactPanel.propTypes = {
  type: PropTypes.string.isRequired,
  patient: PropTypes.shape({
    [ID]: PropTypes.string.isRequired,
    [OTHER_CONTACTS]: PropTypes.arrayOf(
      PropTypes.shape({
        [TITLE]: PropTypes.string,
        [FIRST_NAME]: PropTypes.string,
        [LAST_NAME]: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default ContactPanel;
